/**
 * Trading Store
 * Manages positions, balance, and trading state
 */

import { create } from 'zustand'
import type { Position, Balance, OrderRequest, OrderResult } from '../types'
import { apiClient } from '../services/api'

interface TradingState {
  positions: Position[]
  balance: Balance | null
  isLoading: boolean
  error: string | null

  // Actions
  loadPositions: () => Promise<void>
  loadBalance: () => Promise<void>
  updatePosition: (position: Position) => void
  updateBalance: (balance: Balance) => void
  executeMarketOrder: (order: OrderRequest) => Promise<OrderResult>
  executeLimitOrder: (order: OrderRequest) => Promise<OrderResult>
  closePosition: (dealId: string) => Promise<OrderResult>
  validateOrder: (order: OrderRequest) => Promise<{
    valid: boolean
    error?: string
    fees: number
    availableBalance: number
  }>
  clearError: () => void
}

export const useTradingStore = create<TradingState>((set) => ({
  positions: [],
  balance: null,
  isLoading: false,
  error: null,

  loadPositions: async () => {
    set({ isLoading: true, error: null })
    try {
      const positions = await apiClient.getOpenPositions()
      set({ positions, isLoading: false })
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to load positions',
        isLoading: false,
      })
    }
  },

  loadBalance: async () => {
    try {
      const balance = await apiClient.getBalance()
      set({ balance })
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to load balance' })
    }
  },

  updatePosition: (position: Position) => {
    set((state) => {
      const existingPosition = state.positions.find(p => p.id === position.id)
      
      // Preserve existing PnL temporarily while WebSocket triggers a full REST refresh
      // This prevents UI flicker to $0.00
      if (existingPosition && position.unrealizedPnL === 0 && existingPosition.unrealizedPnL !== 0) {
        position.unrealizedPnL = existingPosition.unrealizedPnL
        position.unrealizedPnLPercent = existingPosition.unrealizedPnLPercent
      }

      const positions = state.positions.filter(p => p.id !== position.id)
      return { positions: [...positions, position] }
    })
  },

  updateBalance: (balance: Balance) => {
    set({ balance })
  },

  executeMarketOrder: async (order: OrderRequest) => {
    set({ isLoading: true, error: null })
    try {
      const result = await apiClient.executeMarketOrder(order)
      set({ isLoading: false })
      return result
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to execute order',
        isLoading: false,
      })
      throw error
    }
  },

  executeLimitOrder: async (order: OrderRequest) => {
    set({ isLoading: true, error: null })
    try {
      const result = await apiClient.executeLimitOrder(order)
      set({ isLoading: false })
      return result
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to execute order',
        isLoading: false,
      })
      throw error
    }
  },

  closePosition: async (dealId: string) => {
    set({ isLoading: true, error: null })
    try {
      const result = await apiClient.closePosition(dealId)
      
      // Reload positions and balance after successful close
      if (result.success) {
        const [positions, balance] = await Promise.all([
          apiClient.getOpenPositions(),
          apiClient.getBalance()
        ])
        set({ positions, balance, isLoading: false })
      } else {
        set({ isLoading: false })
      }
      
      return result
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to close position',
        isLoading: false,
      })
      throw error
    }
  },

  validateOrder: async (order: OrderRequest) => {
    try {
      return await apiClient.validateOrder(order)
    } catch (error: any) {
      throw error
    }
  },

  clearError: () => set({ error: null }),
}))
